import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { PlusCircleIcon, PrinterIcon, DownloadIcon, FileTextIcon, CheckIcon, SettingsIcon, XIcon, SaveIcon, AlertTriangleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface TopBarProps {
  quoteName: string;
  isFinalized: boolean;
  onAddExtras: () => void;
  onPrint: () => void;
  onDownload: () => void;
  currentStep?: number;
  totalSteps?: number;
  designVariant?: string;
  isEditMode?: boolean;
  onCancelEdit?: () => void;
  onCancel?: () => void;
  onSaveDraft?: () => void;
  hideDiscount?: boolean;
  hideMargin?: boolean;
  onToggleHideDiscount?: () => void;
  onToggleHideMargin?: () => void;
  quoteType?: 'dealer' | 'dealer-customer' | 'final-customer';
  quoteId?: string;
  lastUpdated?: string;
}
export function TopBar({
  quoteName,
  isFinalized,
  onAddExtras,
  onPrint,
  onDownload,
  currentStep = 0,
  totalSteps = 5,
  designVariant = 'standard',
  isEditMode = false,
  onCancelEdit,
  onCancel,
  onSaveDraft,
  hideDiscount = false,
  hideMargin = false,
  onToggleHideDiscount,
  onToggleHideMargin,
  quoteType,
  quoteId,
  lastUpdated
}: TopBarProps) {
  const [showModal, setShowModal] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    };
    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu]);
  const steps = [{
    label: 'Pivot Selection',
    shortLabel: 'Pivot'
  }, {
    label: 'Client',
    shortLabel: 'Client'
  }, {
    label: 'Transport',
    shortLabel: 'Transport'
  }, {
    label: 'Quote Info',
    shortLabel: 'Quote Info'
  }, {
    label: 'Preview Quote',
    shortLabel: 'Preview'
  }];
  const handleCloseClick = () => {
    setShowModal(true);
  };
  const handleSaveAndExit = () => {
    if (onSaveDraft) {
      onSaveDraft();
    }
    setShowModal(false);
    if (isEditMode && onCancelEdit) {
      onCancelEdit();
    } else if (onCancel) {
      onCancel();
    }
  };
  const handleDiscardChanges = () => {
    setShowModal(false);
    if (isEditMode && onCancelEdit) {
      onCancelEdit();
    } else if (onCancel) {
      onCancel();
    }
  };
  const handleCancel = () => {
    if (isEditMode && onCancelEdit) {
      onCancelEdit();
    } else if (onCancel) {
      onCancel();
    }
  };
  // Settings Menu Component
  const SettingsMenu = () => {
    if (!showSettingsMenu || quoteType !== 'dealer-customer') return null;
    return <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden" data-id="element-1579">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-200" data-id="element-1580">
          <h3 className="text-sm font-bold text-gray-900" data-id="element-1581">
            Quote Display Settings
          </h3>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1582">
            Control what information is visible to the customer
          </p>
        </div>
        <div className="p-4 space-y-3" data-id="element-1583">
          {/* Hide Discount Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1584">
            <div className="flex items-center gap-3" data-id="element-1585">
              {hideDiscount ? <EyeOffIcon className="w-5 h-5 text-gray-600" data-id="element-1586" /> : <EyeIcon className="w-5 h-5 text-blue-600" data-id="element-1587" />}
              <div data-id="element-1588">
                <p className="text-sm font-semibold text-gray-900" data-id="element-1589">
                  Hide Discount
                </p>
                <p className="text-xs text-gray-600" data-id="element-1590">
                  Hide dealer discount from customer view
                </p>
              </div>
            </div>
            <button onClick={onToggleHideDiscount} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hideDiscount ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1591">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hideDiscount ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1592" />
            </button>
          </div>
          {/* Hide Margin Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1593">
            <div className="flex items-center gap-3" data-id="element-1594">
              {hideMargin ? <EyeOffIcon className="w-5 h-5 text-gray-600" data-id="element-1595" /> : <EyeIcon className="w-5 h-5 text-indigo-600" data-id="element-1596" />}
              <div data-id="element-1597">
                <p className="text-sm font-semibold text-gray-900" data-id="element-1598">
                  Hide Margin
                </p>
                <p className="text-xs text-gray-600" data-id="element-1599">
                  Hide dealer margin from customer view
                </p>
              </div>
            </div>
            <button onClick={onToggleHideMargin} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hideMargin ? 'bg-indigo-600' : 'bg-gray-300'}`} data-id="element-1600">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hideMargin ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1601" />
            </button>
          </div>
        </div>
        <div className="bg-blue-50 p-3 border-t border-blue-200" data-id="element-1602">
          <p className="text-xs text-blue-900" data-id="element-1603">
            <span className="font-semibold" data-id="element-1604">💡 Tip:</span> Use these settings
            when creating quotes in front of customers
          </p>
        </div>
      </div>;
  };
  // Confirmation Modal
  const ConfirmationModal = () => {
    if (!showModal) return null;
    return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1605">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4" data-id="element-1606">
          <div className="p-6" data-id="element-1607">
            <div className="flex items-center gap-4 mb-4" data-id="element-1608">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-1609">
                <AlertTriangleIcon className="w-6 h-6 text-amber-600" data-id="element-1610" />
              </div>
              <div data-id="element-1611">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1612">
                  {isEditMode ? 'Guardar Cambios' : 'Guardar Cotización'}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1613">
                  {isEditMode ? '¿Desea guardar los cambios antes de salir?' : '¿Desea guardar esta cotización como borrador?'}
                </p>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1614">
              <button onClick={handleSaveAndExit} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all font-semibold" data-id="element-1615">
                <SaveIcon className="w-4 h-4" data-id="element-1616" />
                {isEditMode ? 'Guardar y Salir' : 'Guardar como Borrador'}
              </button>
              <button onClick={handleDiscardChanges} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all font-semibold" data-id="element-1617">
                <XIcon className="w-4 h-4" data-id="element-1618" />
                {isEditMode ? 'Descartar Cambios' : 'Cancelar Todo'}
              </button>
              <button onClick={() => setShowModal(false)} className="w-full px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all font-medium" data-id="element-1619">
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  // Deep Ocean Blue Design
  if (designVariant === 'deep-ocean-blue') {
    return <>
        <div className="bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-b-2 border-blue-600/30 shadow-xl" data-id="element-1620">
          <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1621">
            <div className="flex items-center justify-between gap-8" data-id="element-1622">
              {/* Left side - Logo/Title */}
              <div className="flex items-center gap-4 min-w-[250px]" data-id="element-1623">
                <div data-id="element-1624">
                  <div className="flex items-center gap-2" data-id="element-1625">
                    <h2 className="text-sm font-bold text-white" data-id="element-1626">
                      Quote #{quoteId || 'B8TF0001'}
                    </h2>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isEditMode ? 'bg-green-500/20 text-green-300 border border-green-400/30' : 'bg-amber-500/20 text-amber-300 border border-amber-400/30'}`} data-id="element-1627">
                      {isEditMode ? 'Active' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-xs text-blue-200 mt-0.5" data-id="element-1628">
                    {isEditMode ? lastUpdated ? `Updated: ${lastUpdated}` : 'Last edited today' : `Created at ${new Date().toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}`}
                  </p>
                </div>
              </div>
              {/* Center - Stepper */}
              <div className="flex-1 flex items-center justify-center max-w-2xl" data-id="element-1629">
                <div className="flex items-center w-full" data-id="element-1630">
                  {steps.map((step, index) => <Fragment key={index} data-id="element-1631">
                      <div className="flex flex-col items-center" data-id="element-1632">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${index < currentStep ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50' : index === currentStep ? 'bg-white text-[#0a1e3d] shadow-lg shadow-white/30' : 'bg-[#0a1e3d]/50 text-blue-300 border-2 border-blue-600/30'}`} data-id="element-1633">
                          {index < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-1634" /> : index + 1}
                        </div>
                        <span className={`mt-2 text-xs font-medium ${index <= currentStep ? 'text-white' : 'text-blue-300/60'}`} data-id="element-1635">
                          {step.shortLabel}
                        </span>
                      </div>
                      {index < steps.length - 1 && <div className="flex-1 h-0.5 mx-2 mb-6" data-id="element-1636">
                          <div className={`h-full transition-all ${index < currentStep ? 'bg-blue-500 shadow-lg shadow-blue-500/50' : 'bg-[#0a1e3d]/30'}`} data-id="element-1637" />
                        </div>}
                    </Fragment>)}
                </div>
              </div>
              {/* Right side - Settings button only */}
              <div className="flex items-center gap-2 min-w-[200px] justify-end" data-id="element-1638">
                <div className="relative" ref={settingsMenuRef} data-id="element-1639">
                  <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className={`p-2 rounded-lg transition-all ${showSettingsMenu || quoteType === 'dealer-customer' ? 'text-white bg-[#0d3d5c]/50' : 'text-blue-200 hover:text-white hover:bg-[#0d3d5c]/50'}`} title="Settings" data-id="element-1640">
                    <SettingsIcon className="w-5 h-5" data-id="element-1641" />
                  </button>
                  <SettingsMenu data-id="element-1642" />
                </div>
                <button onClick={handleCloseClick} className="p-2 text-blue-200 hover:text-white hover:bg-[#0d3d5c]/50 rounded-lg transition-all" title="Close" data-id="element-1643">
                  <XIcon className="w-5 h-5" data-id="element-1644" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <ConfirmationModal data-id="element-1645" />
      </>;
  }
  // Navy Gradient Design
  if (designVariant === 'navy-gradient') {
    return <div className="bg-gradient-to-r from-blue-900 via-blue-700 to-blue-600 border-b-2 border-blue-500/30 shadow-xl" data-id="element-1646">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1647">
          <div className="flex items-center justify-between gap-8" data-id="element-1648">
            {/* Left side - Logo/Title */}
            <div className="flex items-center gap-4 min-w-[200px]" data-id="element-1649">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-lg" data-id="element-1650">
                <FileTextIcon className="w-6 h-6 text-blue-900" data-id="element-1651" />
              </div>
              <div data-id="element-1652">
                <h2 className="text-sm font-bold text-white" data-id="element-1653">Cotización</h2>
                <p className="text-xs text-blue-200" data-id="element-1654">{quoteName || 'Nueva'}</p>
              </div>
            </div>
            {/* Center - Stepper */}
            <div className="flex-1 flex items-center justify-center max-w-2xl" data-id="element-1655">
              <div className="flex items-center w-full" data-id="element-1656">
                {steps.map((step, index) => <Fragment key={index} data-id="element-1657">
                    <div className="flex flex-col items-center" data-id="element-1658">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${index < currentStep ? 'bg-blue-400 text-blue-950 shadow-lg shadow-blue-400/50' : index === currentStep ? 'bg-white text-blue-900 shadow-lg shadow-white/30' : 'bg-blue-950/50 text-blue-300 border-2 border-blue-500/30'}`} data-id="element-1659">
                        {index < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-1660" /> : index + 1}
                      </div>
                      <span className={`mt-2 text-xs font-medium ${index <= currentStep ? 'text-white' : 'text-blue-300/60'}`} data-id="element-1661">
                        {step.shortLabel}
                      </span>
                    </div>
                    {index < steps.length - 1 && <div className="flex-1 h-0.5 mx-2 mb-6" data-id="element-1662">
                        <div className={`h-full transition-all ${index < currentStep ? 'bg-blue-400 shadow-lg shadow-blue-400/50' : 'bg-blue-950/30'}`} data-id="element-1663" />
                      </div>}
                  </Fragment>)}
              </div>
            </div>
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-2 min-w-[200px] justify-end" data-id="element-1664">
              <button onClick={onPrint} className="p-2 text-blue-200 hover:text-white hover:bg-blue-800/50 rounded-lg transition-all" title="Imprimir" data-id="element-1665">
                <PrinterIcon className="w-5 h-5" data-id="element-1666" />
              </button>
              <button onClick={onDownload} className="p-2 text-blue-200 hover:text-white hover:bg-blue-800/50 rounded-lg transition-all" title="Descargar" data-id="element-1667">
                <DownloadIcon className="w-5 h-5" data-id="element-1668" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Deep Ocean Design
  if (designVariant === 'deep-ocean') {
    return <div className="bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-b border-teal-600/30 shadow-lg" data-id="element-1669">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1670">
          <div className="flex items-center justify-between gap-8" data-id="element-1671">
            {/* Left side - Logo/Title */}
            <div className="flex items-center gap-4 min-w-[200px]" data-id="element-1672">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-1673">
                <FileTextIcon className="w-6 h-6 text-[#0a1e3d]" data-id="element-1674" />
              </div>
              <div data-id="element-1675">
                <h2 className="text-sm font-bold text-white" data-id="element-1676">Cotización</h2>
                <p className="text-xs text-teal-200" data-id="element-1677">{quoteName || 'Nueva'}</p>
              </div>
            </div>
            {/* Center - Stepper */}
            <div className="flex-1 flex items-center justify-center max-w-2xl" data-id="element-1678">
              <div className="flex items-center w-full" data-id="element-1679">
                {steps.map((step, index) => <Fragment key={index} data-id="element-1680">
                    <div className="flex flex-col items-center" data-id="element-1681">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${index < currentStep ? 'bg-teal-400 text-[#0a1e3d] shadow-lg shadow-teal-400/50' : index === currentStep ? 'bg-white text-[#0a1e3d] shadow-lg shadow-white/30' : 'bg-[#0a1e3d]/50 text-teal-300 border-2 border-teal-600/30'}`} data-id="element-1682">
                        {index < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-1683" /> : index + 1}
                      </div>
                      <span className={`mt-2 text-xs font-medium ${index <= currentStep ? 'text-white' : 'text-teal-300/60'}`} data-id="element-1684">
                        {step.shortLabel}
                      </span>
                    </div>
                    {index < steps.length - 1 && <div className="flex-1 h-0.5 mx-2 mb-6" data-id="element-1685">
                        <div className={`h-full transition-all ${index < currentStep ? 'bg-teal-400 shadow-lg shadow-teal-400/50' : 'bg-[#0a1e3d]/30'}`} data-id="element-1686" />
                      </div>}
                  </Fragment>)}
              </div>
            </div>
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-2 min-w-[200px] justify-end" data-id="element-1687">
              <button onClick={onPrint} className="p-2 text-teal-200 hover:text-white hover:bg-[#0d3d5c]/50 rounded-lg transition-all" title="Imprimir" data-id="element-1688">
                <PrinterIcon className="w-5 h-5" data-id="element-1689" />
              </button>
              <button onClick={onDownload} className="p-2 text-teal-200 hover:text-white hover:bg-[#0d3d5c]/50 rounded-lg transition-all" title="Descargar" data-id="element-1690">
                <DownloadIcon className="w-5 h-5" data-id="element-1691" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Ocean Gradient Design
  if (designVariant === 'ocean-gradient') {
    return <div className="bg-gradient-to-r from-blue-950 via-blue-800 to-cyan-700 border-b border-cyan-600/30 shadow-lg" data-id="element-1692">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1693">
          <div className="flex items-center justify-between gap-8" data-id="element-1694">
            {/* Left side - Logo/Title */}
            <div className="flex items-center gap-4 min-w-[200px]" data-id="element-1695">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-1696">
                <FileTextIcon className="w-6 h-6 text-blue-900" data-id="element-1697" />
              </div>
              <div data-id="element-1698">
                <h2 className="text-sm font-bold text-white" data-id="element-1699">Cotización</h2>
                <p className="text-xs text-cyan-200" data-id="element-1700">{quoteName || 'Nueva'}</p>
              </div>
            </div>
            {/* Center - Stepper */}
            <div className="flex-1 flex items-center justify-center max-w-2xl" data-id="element-1701">
              <div className="flex items-center w-full" data-id="element-1702">
                {steps.map((step, index) => <Fragment key={index} data-id="element-1703">
                    <div className="flex flex-col items-center" data-id="element-1704">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${index < currentStep ? 'bg-cyan-400 text-blue-900 shadow-lg shadow-cyan-400/50' : index === currentStep ? 'bg-white text-blue-900 shadow-lg shadow-white/30' : 'bg-blue-900/50 text-cyan-300 border-2 border-cyan-600/30'}`} data-id="element-1705">
                        {index < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-1706" /> : index + 1}
                      </div>
                      <span className={`mt-2 text-xs font-medium ${index <= currentStep ? 'text-white' : 'text-cyan-300/60'}`} data-id="element-1707">
                        {step.shortLabel}
                      </span>
                    </div>
                    {index < steps.length - 1 && <div className="flex-1 h-0.5 mx-2 mb-6" data-id="element-1708">
                        <div className={`h-full transition-all ${index < currentStep ? 'bg-cyan-400 shadow-lg shadow-cyan-400/50' : 'bg-blue-900/30'}`} data-id="element-1709" />
                      </div>}
                  </Fragment>)}
              </div>
            </div>
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-2 min-w-[200px] justify-end" data-id="element-1710">
              <button onClick={onPrint} className="p-2 text-cyan-200 hover:text-white hover:bg-blue-800/50 rounded-lg transition-all" title="Imprimir" data-id="element-1711">
                <PrinterIcon className="w-5 h-5" data-id="element-1712" />
              </button>
              <button onClick={onDownload} className="p-2 text-cyan-200 hover:text-white hover:bg-blue-800/50 rounded-lg transition-all" title="Descargar" data-id="element-1713">
                <DownloadIcon className="w-5 h-5" data-id="element-1714" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Standard Design (existing)
  return <div className="bg-white border-b border-gray-200 shadow-sm" data-id="element-1715">
      <div className="max-w-6xl mx-auto px-4 py-4" data-id="element-1716">
        <div className="flex items-center justify-between" data-id="element-1717">
          <div className="flex items-center gap-4" data-id="element-1718">
            <div data-id="element-1719">
              <div className="flex items-center gap-3" data-id="element-1720">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-1721">
                  {quoteName || 'Nueva Cotización'}
                </h2>
              </div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1722">
                Sistema de Cotización de Repuestos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1723">
            <button onClick={onAddExtras} className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1724">
              <PlusCircleIcon className="w-4 h-4" data-id="element-1725" />
              <span className="font-medium" data-id="element-1726">Agregar Extras</span>
            </button>
            <button onClick={onPrint} className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1727">
              <PrinterIcon className="w-4 h-4" data-id="element-1728" />
              <span className="font-medium" data-id="element-1729">Imprimir</span>
            </button>
            <button onClick={onDownload} className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1730">
              <DownloadIcon className="w-4 h-4" data-id="element-1731" />
              <span className="font-medium" data-id="element-1732">Descargar</span>
            </button>
          </div>
        </div>
      </div>
    </div>;
}